import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '../../components/ui/Card';
import { salesApiService } from '../../api/salesApi';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from '@tanstack/react-table';
import { ArrowLeftIcon, ArrowRightIcon, XMarkIcon, GlobeAltIcon, MapPinIcon, CalendarIcon } from '@heroicons/react/24/outline';

const B2BMeetingsDashboard = () => {
    // --- State ---
    const [year, setYear] = useState(new Date().getFullYear());

    // Data States
    const [markets, setMarkets] = useState([]);
    const [allCountries, setAllCountries] = useState([]); // All fetched countries (for filtering)
    const [allCities, setAllCities] = useState([]); // All fetched cities (for filtering)

    // Selection States
    const [selectedMarketId, setSelectedMarketId] = useState(null);
    const [selectedCountryId, setSelectedCountryId] = useState(null);

    // Grid Data
    const [filteredCountries, setFilteredCountries] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [visitReports, setVisitReports] = useState([]);

    const [loading, setLoading] = useState(false);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null); // { cityId, month, existingReport }
    const [visitCountInput, setVisitCountInput] = useState('');
    const [isBanquetInput, setIsBanquetInput] = useState(false);
    const [selectedHotelIdInput, setSelectedHotelIdInput] = useState('');
    const [hotels, setHotels] = useState([]);


    // --- Loader Effects ---

    // 1. Initial Load: Markets, Countries
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                const [marketsRes, countriesRes, hotelsRes] = await Promise.all([
                    salesApiService.getMarketingMarkets(),
                    salesApiService.getMarketingCountries(), // Get ALL countries with market_id
                    salesApiService.getHotels()
                ]);
                setMarkets(marketsRes);
                setAllCountries(countriesRes);
                setHotels(Array.isArray(hotelsRes) ? hotelsRes : (hotelsRes?.data || hotelsRes?.results || []));

                if (marketsRes.length > 0) setSelectedMarketId(marketsRes[0].id);

            } catch (e) { console.error("Init fetch error", e); }
            finally { setLoading(false); }
        };
        loadInitialData();
    }, []);

    // 2. Filter Countries when Market changes or Data loaded
    useEffect(() => {
        if (!selectedMarketId || allCountries.length === 0) {
            if (allCountries.length > 0) setFilteredCountries(allCountries);
            else setFilteredCountries([]);
            return;
        }

        const filtered = allCountries.filter(c => c.market === selectedMarketId);
        setFilteredCountries(filtered);

        if (filtered.length > 0) {
            if (!filtered.find(c => c.id === selectedCountryId)) {
                setSelectedCountryId(filtered[0].id);
            }
        } else {
            setSelectedCountryId(null);
        }

    }, [selectedMarketId, allCountries, selectedCountryId]);

    // 3. Load Cities when Country Selected
    useEffect(() => {
        const loadcities = async () => {
            if (!selectedCountryId) {
                setFilteredCities([]);
                return;
            }
            setLoading(true);
            try {
                const citiesRes = await salesApiService.getMarketingCities(selectedCountryId);
                setFilteredCities(citiesRes);

                const reportsRes = await salesApiService.getVisitReports({
                    visit_year: year,
                    market: selectedMarketId,
                    country: selectedCountryId
                });

                const finalData = Array.isArray(reportsRes) ? reportsRes : (reportsRes?.results || []);
                setVisitReports(finalData);

            } catch (e) {
                console.error("City/Report Load Error", e);
            } finally {
                setLoading(false);
            }
        };

        loadcities();
    }, [selectedCountryId, year, selectedMarketId]);


    const handlePrevYear = () => setYear(prev => prev - 1);
    const handleNextYear = () => setYear(prev => prev + 1);

    const handleCellClick = (cityId, month) => {
        const reports = Array.isArray(visitReports) ? visitReports : [];
        const existing = reports.find(r =>
            String(r.city) === String(cityId) &&
            Number(r.visit_month) === Number(month)
        );

        setModalData({
            cityId,
            month,
            existingReport: existing || null,
            cityName: filteredCities.find(c => String(c.id) === String(cityId))?.city_name
        });

        setVisitCountInput(existing ? (existing.visit_count || '') : '');
        setIsBanquetInput(existing ? !!existing.banquet : false);
        setSelectedHotelIdInput(existing && existing.hotel ? existing.hotel : '');
        setIsModalOpen(true);
    };

    const handleSaveReport = async () => {
        if (!modalData) return;

        const payload = {
            visit_year: year,
            visit_month: modalData.month,
            visit_count: parseInt(visitCountInput) || 0,
            banquet: isBanquetInput,
            market: selectedMarketId,
            country: selectedCountryId,
            city: modalData.cityId,
            hotel: selectedHotelIdInput ? parseInt(selectedHotelIdInput) : null,
        };

        try {
            if (modalData.existingReport) {
                const res = await salesApiService.updateVisitReport(modalData.existingReport.id, payload);
                setVisitReports(prev => {
                    const reports = Array.isArray(prev) ? prev : [];
                    return reports.map(r => r.id === res.id ? res : r);
                });
            } else {
                const res = await salesApiService.createVisitReport(payload);
                setVisitReports(prev => {
                    const reports = Array.isArray(prev) ? prev : [];
                    return [...reports, res];
                });
            }
            setIsModalOpen(false);
        } catch (e) {
            console.error("Save error detail:", e);
            alert("Kaydedilemedi!");
        }
    };


    const columns = React.useMemo(() => {
        const months = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];

        const cols = [
            {
                accessorKey: 'city_name',
                header: () => <span className="font-bold text-gray-700 dark:text-gray-200">City</span>,
                cell: info => <span className="font-medium text-gray-900 dark:text-white pl-2">{info.getValue()}</span>,
                size: 150,
            }
        ];

        months.forEach((monthName, index) => {
            cols.push({
                id: `month_${index + 1}`,
                header: () => <span className="text-gray-600 dark:text-gray-400 text-xs uppercase">{monthName}</span>,
                size: 80,
                cell: (info) => {
                    const cityId = info.row.original.id;
                    const monthIndex = index + 1;

                    const reports = Array.isArray(visitReports) ? visitReports : [];
                    const report = reports.find(r =>
                        String(r.city) === String(cityId) &&
                        Number(r.visit_month) === Number(monthIndex)
                    );

                    return (
                        <div
                            className="h-full min-h-[50px] w-full flex items-center justify-center cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors border-l border-dashed border-gray-200 dark:border-gray-700 relative group"
                            onClick={() => handleCellClick(cityId, monthIndex)}
                        >
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                                <span className="text-blue-400 text-xl font-light">+</span>
                            </div>

                            {report && (
                                <div className="relative z-10 flex items-center gap-1 bg-white dark:bg-gray-800 shadow-sm px-2 py-1 rounded border border-blue-100 dark:border-blue-900">
                                    <span className="font-bold text-blue-600 dark:text-blue-400 text-sm">{report.visit_count}</span>
                                    {report.banquet && (
                                        <span className="text-orange-500 text-xs" title="Yemekli">🍽️</span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                }
            });
        });
        return cols;
    }, [visitReports, year]);


    const table = useReactTable({
        data: filteredCities,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });


    // --- Render ---
    return (
        <div className="space-y-6 relative">
            <div className="px-6 pt-4 space-y-6">
                <div className="flex flex-col gap-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sales Call Visit Report</h1>
                        <p className="text-sm text-gray-500 font-medium">Manage and track your b2b meeting activities</p>
                    </div>

                    {/* Filters - Unified Lookup Style */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
                        {/* Market Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Market</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                    <GlobeAltIcon className="h-4 w-4" />
                                </div>
                                <select
                                    value={selectedMarketId || ''}
                                    onChange={(e) => setSelectedMarketId(Number(e.target.value))}
                                    className="block w-full pl-10 pr-4 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:text-white transition-all appearance-none cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 shadow-sm"
                                >
                                    <option value="" disabled>Select Market</option>
                                    {markets.map(m => (
                                        <option key={m.id} value={m.id}>{m.budget_market_name}</option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </div>
                            </div>
                        </div>

                        {/* Country Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Country</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                    <MapPinIcon className="h-4 w-4" />
                                </div>
                                <select
                                    value={selectedCountryId || ''}
                                    onChange={(e) => setSelectedCountryId(Number(e.target.value))}
                                    className="block w-full pl-10 pr-4 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:text-white transition-all appearance-none cursor-pointer disabled:opacity-50 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm"
                                    disabled={!selectedMarketId}
                                >
                                    <option value="" disabled>{selectedMarketId ? 'Select Country' : 'First select market'}</option>
                                    {filteredCountries.map(c => (
                                        <option key={c.id} value={c.id}>{c.country_name}</option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </div>
                            </div>
                        </div>

                        {/* Year Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Selected Year</label>
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm px-3 py-1.5 border border-gray-200 dark:border-gray-700 h-[38px]">
                                <button onClick={handlePrevYear} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-blue-500">
                                    <ArrowLeftIcon className="w-4 h-4" />
                                </button>
                                <div className="flex items-center gap-2 flex-1 justify-center px-4 border-x border-gray-100 dark:border-gray-700">
                                    <CalendarIcon className="w-4 h-4 text-blue-500" />
                                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200 select-none">{year}</span>
                                </div>
                                <button onClick={handleNextYear} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-blue-500">
                                    <ArrowRightIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 pb-8">
                <Card className="bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
                    {loading && filteredCities.length === 0 ? (
                        <div className="h-64 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : filteredCities.length === 0 ? (
                        <div className="h-64 flex items-center justify-center text-gray-400">
                            {selectedCountryId ? "Bu ülke için şehir tanımı yok." : "Lütfen bir ülke seçiniz."}
                        </div>
                    ) : (
                        <div className="overflow-auto max-h-[calc(100vh-280px)]">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id} className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                                            {headerGroup.headers.map(header => (
                                                <th
                                                    key={header.id}
                                                    className="sticky top-0 z-20 p-3 text-left font-medium bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 first:z-30 first:left-0 first:shadow-[1px_1px_0_0_rgba(0,0,0,0.05)] dark:first:shadow-[1px_1px_0_0_rgba(255,255,255,0.05)]"
                                                    style={{ width: header.getSize() }}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map(row => (
                                        <tr key={row.id} className="border-b border-dashed border-gray-200 dark:border-gray-700 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                                            {row.getVisibleCells().map(cell => (
                                                <td
                                                    key={cell.id}
                                                    className="p-0 first:sticky first:left-0 first:z-10 first:bg-white dark:first:bg-gray-800 first:shadow-[1px_0_0_0_rgba(0,0,0,0.05)] dark:first:shadow-[1px_0_0_0_rgba(255,255,255,0.05)] align-top"
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {/* Visit Report Modal - Rendered via Portal to cover entire screen */}
            {isModalOpen && modalData && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm transform transition-all scale-100 border border-gray-100 dark:border-gray-700">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Ziyaret Raporu</h3>
                                <p className="text-xs text-gray-500">{modalData.cityName} - {year}/{modalData.month}</p>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Ziyaret Sayısı
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-semibold text-center"
                                    value={visitCountInput}
                                    onChange={(e) => setVisitCountInput(e.target.value)}
                                    placeholder="0"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Otel
                                </label>
                                <select
                                    value={selectedHotelIdInput}
                                    onChange={(e) => setSelectedHotelIdInput(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Seçiniz (Opsiyonel)</option>
                                    {hotels.map(h => (
                                        <option key={h.id} value={h.id}>{h.hotel_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-100 dark:border-gray-600 cursor-pointer" onClick={() => setIsBanquetInput(!isBanquetInput)}>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                    <span className="text-xl">🍽️</span> Yemekli
                                </span>
                                <div className={`w-11 h-6 flex items-center rounded-full transition-colors ${isBanquetInput ? 'bg-blue-600' : 'bg-gray-300'}`}>
                                    <div className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform ${isBanquetInput ? 'translate-x-6' : 'translate-x-1'}`} />
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                onClick={handleSaveReport}
                                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md transition-colors"
                            >
                                Kaydet
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default B2BMeetingsDashboard;
